import {BehaviorSubject, combineLatest, map, Observable} from 'rxjs';
import {inject, injectable} from 'inversify';
import {Elements} from 'react-flow-renderer';
import {
  GlobalContainerTypes,
  RoomResponse,
  WorkspaceResponse,
  WorkspacesNormalization,
  FloorsNormalization,
  MeetingRoomNormalization,
} from '@innowise-group/core';
import {notificationsManager} from '@innowise-group/ui-kit';
import {PlanElementData} from '../../components/plan-element-view';
import {WorkspaceElementData} from '../../components/workspace-view';
import {FloorsContainerTypes} from '../floors-ioc.types';
import {FloorElements, FloorsDataManager} from './floors-data-manager.types';
import {mapWorkspaceToElements} from '../../utilities/element-mapper.utility';
import {pointInPolygon} from '../../utilities/check-workspace-room-bounds.utility';
import {determineRoomMeetingId} from '@shared/utilities';
import {TypeRoomEnum} from '@shared/utilities/determine-room-meeting-id.utility';
import {MeetingRoomResponse} from '../floors-event-emitter';
import _ from 'lodash';

@injectable()
class FloorsDataManagerService implements FloorsDataManager {
  public static readonly type = FloorsContainerTypes.FloorsDataManager;
  @inject(GlobalContainerTypes.WorkspacesNormalization) private workspacesNormalization: WorkspacesNormalization;
  @inject(GlobalContainerTypes.FloorsNormalization) private floorsNormalization: FloorsNormalization;
  @inject(GlobalContainerTypes.MeetingRoomNormalization) private meetingRoomNormalization: MeetingRoomNormalization;

  public workspaces$: BehaviorSubject<Elements<WorkspaceElementData>>;
  public rooms$: BehaviorSubject<Elements<PlanElementData>>;
  private stairs$: BehaviorSubject<Elements<PlanElementData>>;
  private balconies$: BehaviorSubject<Elements<PlanElementData>>;
  private lifts$: BehaviorSubject<Elements<PlanElementData>>;
  private doors$: BehaviorSubject<Elements<PlanElementData>>;
  // private columns$: BehaviorSubject<Elements<PlanElementData>>;

  public initialWorkspaces$: BehaviorSubject<Elements<WorkspaceElementData>>;
  public createdWorkspaces$: BehaviorSubject<Elements<WorkspaceElementData>>;
  public deletedWorkspaces$: BehaviorSubject<Elements<WorkspaceElementData>>;

  constructor() {
    this.workspaces$ = new BehaviorSubject([]);
    this.rooms$ = new BehaviorSubject([]);
    this.stairs$ = new BehaviorSubject([]);
    this.balconies$ = new BehaviorSubject([]);
    this.lifts$ = new BehaviorSubject([]);
    this.doors$ = new BehaviorSubject([]);
    // this.columns$ = new BehaviorSubject([]);

    this.initialWorkspaces$ = new BehaviorSubject([]);
    this.createdWorkspaces$ = new BehaviorSubject([]);
    this.deletedWorkspaces$ = new BehaviorSubject([]);

    this.createWorkspaceSubscription = this.createWorkspaceSubscription.bind(this);
    this.initialize = this.initialize.bind(this);
    this.applyViewMode = this.applyViewMode.bind(this);
    this.setWorkspacesSubscription = this.setWorkspacesSubscription.bind(this);
    this.deleteWorkspaceSubscription = this.deleteWorkspaceSubscription.bind(this);
    this.editWorkspaceSubscription = this.editWorkspaceSubscription.bind(this);
    this.editRoomSubscription = this.editRoomSubscription.bind(this);
    this.deleteRoomSubscription = this.deleteRoomSubscription.bind(this);
    this.deleteDoorSubscription = this.deleteDoorSubscription.bind(this);
    this.errorSubscription = this.errorSubscription.bind(this);
    this.getNormalizeWorkspaceElement = this.getNormalizeWorkspaceElement.bind(this);
    this.getWorkspaceByIdSubscription = this.getWorkspaceByIdSubscription.bind(this);
    this.updateMeetingRoomInRooms = this.updateMeetingRoomInRooms.bind(this);
  }

  private getDraftWorkspaces(): Elements<WorkspaceElementData> {
    return this.workspaces$.getValue().filter((element) => element.data?.isDraft);
  }

  private withPreservedDrafts(workspaces: Elements<WorkspaceElementData>): Elements<WorkspaceElementData> {
    const drafts = this.getDraftWorkspaces();
    if (!drafts.length) {
      return workspaces;
    }
    return [...workspaces, ...drafts];
  }

  public initialize(elements: FloorElements): void {
    Object.keys(elements).forEach((key) => {
      if (key === 'workspaces') {
        this.workspaces$.next(this.withPreservedDrafts(elements.workspaces || []));
        return;
      }
      this[`${key}$`].next(elements[key]);
    });
    // Baselines must not include local drafts that are not persisted yet.
    this.initialWorkspaces$.next(elements.workspaces || []);
  }

  public createWorkspaceSubscription(workspace: WorkspaceResponse, isViewMode: boolean, showNotification: boolean) {
    if (showNotification) {
      notificationsManager.success({title: 'success', subtitle: 'createWorkspace'});
    }
    const createdWorkspaceElement = this.getNormalizeWorkspaceElement([workspace], isViewMode);
    const workspacesElements = this.workspaces$.getValue();
    this.workspaces$.next([...workspacesElements, ...createdWorkspaceElement]);
    const createdWorkspacesElements = this.createdWorkspaces$.getValue();
    this.createdWorkspaces$.next([...createdWorkspacesElements, ...createdWorkspaceElement]);
  }

  public setWorkspacesSubscription(workspaces: WorkspaceResponse[] = [], isViewMode: boolean) {
    const workspacesElements = this.getNormalizeWorkspaceElement(workspaces, isViewMode);
    this.workspaces$.next(this.withPreservedDrafts(workspacesElements));
  }

  public applyViewMode(isViewMode: boolean): void {
    const workspaces = this.workspaces$.getValue();
    this.workspaces$.next(
      workspaces.map((element) => ({
        ...element,
        data: {...element.data, isViewMode},
        draggable: element.data?.isSaving ? false : !isViewMode,
      })),
    );

    const rooms = this.rooms$.getValue();
    this.rooms$.next(
      rooms.map((element) => ({
        ...element,
        data: {...element.data, isViewMode},
      })),
    );

    const doors = this.doors$.getValue();
    this.doors$.next(
      doors.map((element) => ({
        ...element,
        data: {...element.data, isViewMode},
        selectable: !isViewMode,
      })),
    );
  }

  public deleteWorkspaceSubscription(
    workspace: WorkspaceResponse,
    isViewMode: boolean,
    workspaceId: string,
    showNotification: boolean,
  ): void {
    if (showNotification) {
      notificationsManager.success({title: 'success', subtitle: 'deleteWorkspace'});
    }
    const deletedWorkspaceElement = this.getNormalizeWorkspaceElement([{...workspace, id: +workspaceId}], isViewMode);
    const workspacesElements = this.workspaces$.getValue();
    const restElements = workspacesElements.filter((element) => element.data.id !== workspaceId);
    this.workspaces$.next(restElements);
    const deletedWorkspacesElements = this.deletedWorkspaces$.getValue();
    this.deletedWorkspaces$.next([...deletedWorkspacesElements, ...deletedWorkspaceElement]);
    const initialWorkspaces = this.initialWorkspaces$.getValue();
    const filteredInitialWorkspaces = initialWorkspaces.filter((element) => element.data.id !== workspaceId);
    this.initialWorkspaces$.next(filteredInitialWorkspaces);
  }

  public editWorkspaceSubscription(
    workspace: WorkspaceResponse,
    isViewMode: boolean,
    withNotification = false,
    showNotification: boolean,
  ): void {
    if (withNotification && showNotification) {
      notificationsManager.success({title: 'success', subtitle: 'updateWorkspace'});
    }
    const editedWorkspaceElement = this.getNormalizeWorkspaceElement([workspace], isViewMode)[0];
    const workspacesElements = this.workspaces$.getValue();
    const newWorkspacesElements = workspacesElements.map((workspace) => {
      if (workspace.data.id !== editedWorkspaceElement.data.id) {
        return workspace;
      }

      return {
        ...editedWorkspaceElement,
        data: {...editedWorkspaceElement.data, isSaving: false},
        draggable: !isViewMode,
      };
    });
    this.workspaces$.next(newWorkspacesElements);
  }

  public editRoomSubscription(room: RoomResponse, showNotifications: boolean) {
    if (showNotifications) {
      notificationsManager.success({title: 'success', subtitle: 'editRoom'});
    }
    const roomElements = this.rooms$.getValue();
    const newRoomElements = roomElements.map(({id, data, ...rest}) => {
      if (id === `rooms-${room.id}`) {
        const meetingId = determineRoomMeetingId(room);

        if ('meetingRoomId' in data && room.room_type !== TypeRoomEnum.Meeting) {
          delete data.meetingRoomId;
        }

        return {
          id,
          data: {
            ...data,
            number: room.number,
            roomType: room.room_type,
            isOur: room.our,
            ...meetingId,
          },
          ...rest,
        };
      }
      return {id, data, ...rest};
    });
    this.rooms$.next(newRoomElements);
  }

  public deleteRoomSubscription(roomId: string, doorIds: Array<string | number> = []) {
    const roomIdStr = String(roomId);
    const roomElementId = `rooms-${roomIdStr}`;
    const roomElements = this.rooms$.getValue();
    const roomToDelete = roomElements.find(
      (element) => element.id === roomElementId || String(element.data?.id) === roomIdStr,
    );
    const polygon = roomToDelete?.data?.polygon;

    this.rooms$.next(
      roomElements.filter((element) => element.id !== roomElementId && String(element.data?.id) !== roomIdStr),
    );

    if (doorIds.length) {
      const doorIdSet = new Set(doorIds.map(String));
      const doorElements = this.doors$.getValue();
      this.doors$.next(doorElements.filter((element) => !doorIdSet.has(String(element.data?.id))));
    }

    const isWorkspaceInDeletedRoom = (element: {id?: string; data?: WorkspaceElementData}): boolean => {
      if (String(element.data?.room?.id) === roomIdStr) {
        return true;
      }

      // Drafts / orphans have no room link — remove if their center sits in the deleted room.
      const isDraftOrOrphan = Boolean(element.data?.isDraft) || !element.data?.room?.id;
      if (!isDraftOrOrphan || !polygon || polygon.length < 3) {
        return false;
      }

      const center = element.data?.coordinates?.[0];
      if (!center || center.length < 2) {
        return false;
      }

      return pointInPolygon({x: Number(center[0]), y: Number(center[1])}, polygon);
    };

    const workspacesElements = this.workspaces$.getValue();
    const remainingWorkspaces = workspacesElements.filter((element) => !isWorkspaceInDeletedRoom(element));
    const removedWorkspaces = workspacesElements.filter((element) => isWorkspaceInDeletedRoom(element));
    this.workspaces$.next(remainingWorkspaces);

    if (removedWorkspaces.length) {
      const removedIds = new Set(removedWorkspaces.map((element) => String(element.data?.id ?? element.id)));
      const deletedWorkspacesElements = this.deletedWorkspaces$.getValue();
      this.deletedWorkspaces$.next([...deletedWorkspacesElements, ...removedWorkspaces]);
      const initialWorkspaces = this.initialWorkspaces$.getValue();
      this.initialWorkspaces$.next(
        initialWorkspaces.filter((element) => !removedIds.has(String(element.data?.id ?? element.id))),
      );
      const createdWorkspaces = this.createdWorkspaces$.getValue();
      this.createdWorkspaces$.next(
        createdWorkspaces.filter((element) => !removedIds.has(String(element.data?.id ?? element.id))),
      );
    }
  }

  public deleteDoorSubscription(doorId: string) {
    const doorIdStr = String(doorId);
    const doorElements = this.doors$.getValue();
    this.doors$.next(doorElements.filter((element) => element.data?.id !== doorIdStr));
  }

  public updateMeetingRoomInRooms(meetingroom: MeetingRoomResponse) {
    if (!meetingroom) {
      return;
    }
    const responseKey = Object.keys(meetingroom)[0];
    const meetingRoomId = responseKey?.split('@')[0];

    const meetingRoom = this.meetingRoomNormalization.normalizeMeetingRoomStatus(meetingroom[responseKey]);

    const roomElements = this.rooms$.getValue();
    let isMeetingRoomUpdated = false;

    const newRoomElements = roomElements.map(({id, data, ...rest}) => {
      if (meetingRoomId === data?.meetingRoomId && !_.isEqual(meetingRoom, data?.meetingRoomStatus)) {
        isMeetingRoomUpdated = true;
        return {
          id,
          data: {
            ...data,
            meetingRoomStatus: meetingRoom,
          },
          ...rest,
        };
      }
      return {id, data, ...rest};
    });

    if (isMeetingRoomUpdated) {
      this.rooms$.next(newRoomElements);
    }
  }

  public errorSubscription(status_code: number, error: {[key: string]: any} | string, showNotification: boolean) {
    if (showNotification) {
      notificationsManager.error({
        title: `${status_code >= 400 ? 'statusTextError' : 'networkError'}`,
        subtitle: `${
          typeof error === 'object'
            ? Object.entries(error).reduce((accumulator, [key, value]) => accumulator + `${key}: ${value}`, '')
            : error
        }`,
        statusCode: `${status_code}`,
        isCustomNotification: false,
      });
    }
  }

  public getWorkspaceByIdSubscription(workspace: WorkspaceResponse, isViewMode: boolean) {
    const retrieveWorkspace = this.getNormalizeWorkspaceElement([workspace], isViewMode)[0];
    const workspacesElements = this.workspaces$.getValue();
    const newWorkspacesElements = workspacesElements.map((workspace) => {
      if (workspace.data.id !== retrieveWorkspace.data.id) {
        return workspace;
      }

      return {
        ...retrieveWorkspace,
        data: {...retrieveWorkspace.data, isSaving: false},
        draggable: !isViewMode,
      };
    });
    this.workspaces$.next(newWorkspacesElements);
  }

  public get planElements$(): Observable<Elements<PlanElementData>> {
    return combineLatest([this.stairs$, this.balconies$, this.lifts$, this.doors$]).pipe(
      map((data) => data.reduce((accumulator, array) => accumulator.concat(array), [])),
    );
  }

  private getNormalizeWorkspaceElement(
    workspaces: WorkspaceResponse[],
    isViewMode: boolean,
  ): Elements<WorkspaceElementData> {
    const normalizeWorkspaces = this.workspacesNormalization.normalizeWorkspaceFromApi(workspaces, 'workspaces');
    return mapWorkspaceToElements(normalizeWorkspaces, isViewMode);
  }
}

export default FloorsDataManagerService;
